import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserOrganization } from "@/hooks/use-user-organization";

export interface EnhancedCreditPackage {
  id: string;
  device_id: string;
  package_name: string;
  total_credits: number;
  assigned_credits: number;
  remaining_credits: number;
  is_assigned: boolean;
  assignment_type: 'manual' | 'order_based' | 'admin_override' | 'user_owned';
  purchaser_user_id?: string;
  order_id?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  // Multi-year credit package fields
  year_number?: number;
  release_date?: string;
  is_released?: boolean;
  coverage_year?: number;
  // Enhanced order information
  order_info?: {
    total_cameras: number;
    total_images: number;
    credits_per_camera?: number;
  };
}

export interface DeviceAssignmentAuditEntry {
  id: string;
  device_id: string;
  user_id?: string;
  assignment_type: string;
  action: string;
  conflict_detected?: boolean;
  conflict_reason?: string;
  metadata?: any;
  created_at: string;
}

export interface CreditAllocation {
  id: string;
  device_id: string;
  user_id: string;
  assignment_id: string;
  allocated_credits: number;
  allocated_at: string;
  source_package_id?: string;
}

export const useEnhancedCreditPackages = (deviceId?: string) => {
  const { data: organizationId } = useUserOrganization();

  return useQuery({
    queryKey: ['enhanced-credit-packages', deviceId, organizationId],
    queryFn: async () => {
      let query = supabase
        .from('device_credit_packages')
        .select(`
          *,
          orders (
            total_cameras,
            total_images
          )
        `)
        .order('created_at', { ascending: false });

      if (deviceId) {
        query = query.eq('device_id', deviceId);
      }

      // Always filter by organization_id for security - never allow cross-organization visibility
      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      } else {
        // If no organizationId, return empty results to prevent data leakage
        return [];
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Enhance the data with camera information from joined orders data
      const enhancedData = (data || []).map(pkg => {
        let orderInfo = undefined;
        
        // Check if we have nested orders data from the join
        if (pkg.orders && (pkg.orders.total_cameras || pkg.orders.total_images)) {
          orderInfo = {
            total_cameras: pkg.orders.total_cameras || 0,
            total_images: pkg.orders.total_images || 0,
            credits_per_camera: pkg.orders.total_cameras > 0 
              ? Math.floor(pkg.total_credits / pkg.orders.total_cameras)
              : undefined
          };
        }

        return {
          ...pkg,
          orders: undefined, // Remove the nested orders object
          order_info: orderInfo
        } as EnhancedCreditPackage;
      });
      
      return enhancedData;
    },
    enabled: !!organizationId,
  });
};

export const useUnassignedCreditPackages = () => {
  const { data: organizationId } = useUserOrganization();

  return useQuery({
    queryKey: ['unassigned-credit-packages', organizationId],
    queryFn: async () => {
      let query = supabase
        .from('device_credit_packages')
        .select('*')
        .eq('is_assigned', false)
        .gt('remaining_credits', 0)
        .order('created_at', { ascending: false });

      // Always filter by organization_id for security - never allow cross-organization visibility
      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      } else {
        // If no organizationId, return empty results to prevent data leakage
        return [];
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EnhancedCreditPackage[] || [];
    },
    enabled: !!organizationId,
  });
};

export const useCreditAllocations = (deviceId?: string) => {
  return useQuery({
    queryKey: ['credit-allocations', deviceId],
    queryFn: async () => {
      let query = supabase
        .from('device_assignment_credits')
        .select('*')
        .order('allocated_at', { ascending: false });

      if (deviceId) {
        query = query.eq('device_id', deviceId);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return data as CreditAllocation[] || [];
    },
  });
};

export const useCreateCreditPackage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: organizationId } = useUserOrganization();

  return useMutation({
    mutationFn: async (packageData: {
      device_id: string;
      package_name: string;
      total_credits: number;
      remaining_credits: number;
      assignment_type: 'manual' | 'order_based' | 'admin_override' | 'user_owned';
      purchaser_user_id?: string;
      organization_id?: string;
    }) => {
      const { data, error } = await supabase
        .from('device_credit_packages')
        .insert([{
          device_id: packageData.device_id,
          package_name: packageData.package_name,
          total_credits: packageData.total_credits,
          assigned_credits: 0,
          remaining_credits: packageData.remaining_credits,
          assignment_type: packageData.assignment_type,
          purchaser_user_id: packageData.purchaser_user_id,
          organization_id: packageData.organization_id || organizationId,
          is_assigned: false
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-credit-packages'] });
      toast({
        title: "Success",
        description: "Credit package created successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Error creating credit package:", error);
      toast({
        title: "Error",
        description: "Failed to create credit package. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeviceAssignmentAudit = (deviceId?: string) => {
  return useQuery({
    queryKey: ['device-assignment-audit', deviceId],
    queryFn: async () => {
      let query = supabase
        .from('device_assignment_audit')
        .select('*')
        .order('created_at', { ascending: false });

      if (deviceId) {
        query = query.eq('device_id', deviceId);
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return data as DeviceAssignmentAuditEntry[] || [];
    },
  });
};

export const useCombinedDeviceAssignment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: organizationId } = useUserOrganization();

  return useMutation({
    mutationFn: async (params: {
      deviceId: string;
      userId: string;
      creditPackageId: string;
      assignmentType: 'manual' | 'order_based' | 'admin_override' | 'user_owned';
      validatePurchaser?: boolean;
    }) => {
      console.log('Combined device assignment params:', params);

      // Use upsert to handle device assignment (now that we have unique constraint)
      const { error: assignmentError } = await supabase
        .from('device_assignments')
        .upsert({
          device_id: params.deviceId,
          user_id: params.userId,
          assigned_at: new Date().toISOString()
        }, {
          onConflict: 'device_id,user_id'
        });

      if (assignmentError) {
        console.error('Device assignment error:', assignmentError);
        throw assignmentError;
      }

      console.log('✅ Device assignment created/updated successfully');

      // Get the source credit package to allocate from
      const { data: sourcePackage, error: packageFetchError } = await supabase
        .from('device_credit_packages')
        .select('*')
        .eq('id', params.creditPackageId)
        .single();

      if (packageFetchError) {
        console.error('Package fetch error:', packageFetchError);
        throw packageFetchError;
      }

      // Check if there's already a device-specific credit package for this device
      const { data: existingPackages, error: existingPackageError } = await supabase
        .from('device_credit_packages')
        .select('*')
        .eq('device_id', params.deviceId)
        .eq('is_assigned', true)
        .neq('device_id', 'USER_OWNED');

      if (existingPackageError) {
        console.error('Error checking existing packages:', existingPackageError);
        throw existingPackageError;
      }

      if (existingPackages && existingPackages.length > 0) {
        // Update existing package by adding new credits
        const existingPackage = existingPackages[0];
        const { error: updateError } = await supabase
          .from('device_credit_packages')
          .update({
            total_credits: existingPackage.total_credits + sourcePackage.remaining_credits,
            assigned_credits: existingPackage.assigned_credits + sourcePackage.remaining_credits,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPackage.id);

        if (updateError) {
          console.error('Package update error:', updateError);
          throw updateError;
        }
      } else {
        // Create new device-specific package only if none exists
        const { error: packageCreateError } = await supabase
          .from('device_credit_packages')
          .insert({
            device_id: params.deviceId,
            package_name: `Allocated from ${sourcePackage.package_name}`,
            total_credits: sourcePackage.remaining_credits,
            assigned_credits: sourcePackage.remaining_credits,
            remaining_credits: 0,
            assignment_type: params.assignmentType,
            purchaser_user_id: sourcePackage.purchaser_user_id,
            organization_id: organizationId,
            is_assigned: true,
            order_id: sourcePackage.order_id
          });

        if (packageCreateError) {
          console.error('Package create error:', packageCreateError);
          throw packageCreateError;
        }
      }

      // Mark the original user-owned package as fully allocated
      const { error: originalPackageError } = await supabase
        .from('device_credit_packages')
        .update({
          assigned_credits: sourcePackage.total_credits,
          remaining_credits: 0,
          is_assigned: true
        })
        .eq('id', params.creditPackageId);

      if (originalPackageError) {
        console.error('Original package update error:', originalPackageError);
        throw originalPackageError;
      }

      // Get current images_allowed and increment it
      const { data: deviceData, error: deviceFetchError } = await supabase
        .from('device_details')
        .select('images_allowed')
        .eq('device_id', params.deviceId)
        .single();

      if (deviceFetchError) {
        console.error('Device fetch error:', deviceFetchError);
        throw deviceFetchError;
      }

      const currentCredits = deviceData?.images_allowed || 0;
      const newTotal = currentCredits + sourcePackage.remaining_credits;

      const { error: deviceUpdateError } = await supabase
        .from('device_details')
        .update({
          images_allowed: newTotal
        })
        .eq('device_id', params.deviceId);

      if (deviceUpdateError) {
        console.error('Device update error:', deviceUpdateError);
        throw deviceUpdateError;
      }

      // Add audit log entry for manual credit allocation
      const { error: auditError } = await supabase
        .from('device_assignment_audit')
        .insert({
          device_id: params.deviceId,
          user_id: params.userId,
          assignment_type: params.assignmentType,
          action: 'manual_credit_allocation',
          metadata: {
            credit_package_id: params.creditPackageId,
            credits_allocated: sourcePackage.remaining_credits,
            package_name: sourcePackage.package_name,
            previous_device_credits: currentCredits,
            new_device_credits: newTotal,
            allocation_timestamp: new Date().toISOString(),
            allocated_by: params.userId, // The user performing the allocation
            manual_allocation: true
          }
        });

      if (auditError) {
        console.error('Audit log error:', auditError);
        // Don't throw here - we don't want to fail the allocation if audit logging fails
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-credit-packages'] });
      queryClient.invalidateQueries({ queryKey: ['device-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['device-details'] });
      
      toast({
        title: "Success",
        description: "Credits allocated to device successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Combined assignment error:", error);
      toast({
        title: "Error",
        description: "Failed to complete device assignment and credit allocation. Please try again.",
        variant: "destructive",
      });
    },
  });
};
